import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePageTitle } from "hooks/usePageTitle";
import { UserData } from "modules/authentication/types/user-data";

import { useMediaQuery } from "@/hooks/use-media-query";
import { cn } from "@/lib/utils";


import { useAuth } from "modules/authentication/hooks/use-auth";
import { ListsDashboard } from "modules/lists/components/ListsDashboard";
import { useLists } from "modules/lists/context/ListsContext";
import { usePersistOnboardingData } from "modules/onboarding/hooks/use-persist-onboarding-data";
import { readOnboardingState } from "modules/onboarding/types/onboarding-state";
import { computeSavings, getMonthlyMultiplier } from "modules/onboarding/components/step-five";
import { saveOnboardingTargetPrompt } from "modules/lists/utils/onboarding-target-prompt";

import { useFeatureFlagEnabled } from "posthog-js/react";
import { UsersDashboard } from "modules/users/components/user-dashboard";

const getUserData = () => {
  try {
    const data = localStorage.getItem("userData");
    return data ? (JSON.parse(data) as UserData) : ({} as UserData);
  } catch (error) {
    console.warn("Error reading userData:", error);
    return {} as UserData;
  }
};
export const MainPage = () => {
  const { setTitle,setShowBack } = usePageTitle();
  const { isLogged, isPremiumUser } = useAuth();
  const { lists, createList } = useLists();
  const navigate = useNavigate();
  const [userData, setUserData] = useState<UserData>({} as UserData);

  useEffect(() => {
    setTitle("Mi panel de ahorro");
    setShowBack(false);
  }, [setTitle, setShowBack]);

  useEffect(() => {
    const removeRecaptchaBadge = () => {
      document
        .querySelectorAll(".grecaptcha-badge")
        .forEach((badge) => badge.remove());
    };

    removeRecaptchaBadge();

    const observer = new MutationObserver(removeRecaptchaBadge);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
    };
  }, []);

  const [userIsConfigured, setUserIsConfigured] = useState(
    userData.settings_set,
  );
  const isDesktop = useMediaQuery("(min-width: 1024px)");

  useEffect(() => {
    //cargar indicador de primer uso
    try {
      localStorage.setItem("visitor", "true");
    } catch (error) {
      console.warn("Error writing visitor flag:", error);
    }

    // Cargar userData actualizado
    const currentUserData = getUserData();
    setUserData(currentUserData);
    setUserIsConfigured(currentUserData.settings_set || false);
  }, []);

  const onboardingSnapshot = useMemo(() => {
    const draft = readOnboardingState();
    if (!draft?.spend_per_purchase || !draft?.purchase_frequency) return null;
    const effectiveMonthly = draft.spend_per_purchase * getMonthlyMultiplier(draft.purchase_frequency);
    const projection = computeSavings(effectiveMonthly, draft.purchase_frequency);
    const savingsPerPurchase = Math.round(draft.spend_per_purchase * projection.savingsRate);
    return { spendPerPurchase: draft.spend_per_purchase, savingsPerPurchase };
  }, []);

  /**
   * Fire-and-forget side effect: persist the onboarding draft to the
   * backend once the authenticated user lands on /store and the auth
   * contract says they completed the wizard. The hook self-guards on
   * prerequisites and dedupes StrictMode double-mounts.
   */
  const handleOnboardingPersisted = useCallback(async () => {
    if (lists.length === 0) {
      const newList = await createList("Mi primera lista en Carriapp", "");
      if (onboardingSnapshot) {
        saveOnboardingTargetPrompt(newList.id, onboardingSnapshot);
      }
      navigate(`/lists/${newList.id}`, { state: { isNew: true } });
    }
  }, [createList, lists.length, navigate, onboardingSnapshot]);

  const onboardingPersistenceRetrySignal = `${isLogged}:${userData.id ?? "anonymous"}`;

  usePersistOnboardingData(
    handleOnboardingPersisted,
    onboardingPersistenceRetrySignal,
  );


  //feature flag, remove in future iterations
  const flagEnabled = useFeatureFlagEnabled("list-release");

  return (
    <>
      <div className="noto-sans-regular bg-[#F8F8F8] h-full">
        <div className="flex flex-col w-full 2xl:w-[80%] mx-auto px-4 mt-8  pb-8 gap-8">
          {isLogged && flagEnabled && (
            <>
              <div className="flex flex-col w-full gap-4">
                <h2
                  className={cn(
                    "font-bold",
                    {
                      "text-3xl": isDesktop,
                      " text-2xl": !isDesktop,
                    },
                  )}
                >
                  <span>¡Hola {userData.first_name || "Usuario"}!</span>
                  {isPremiumUser && (
                    <span className="ml-2 inline-flex h-4 items-center rounded-full border border-border/50 bg-background px-1.5 align-middle text-[15px] font-semibold leading-none">
                      <span className="bg-[linear-gradient(90deg,#F9802D_0%,#F9802D_42%,#5CA273_100%)] bg-[length:220%_220%] bg-clip-text text-transparent animate-[ai-border-flow_8s_cubic-bezier(0.4,0,0.2,1)_infinite]">
                        +Plus
                      </span>
                    </span>
                  )}
                </h2>
                <p className="text-xl text-muted-foreground">
                  ¿En qué vamos a ahorrar hoy?
                </p>
              </div>
            </>
          )}
          <UsersDashboard />
          <div>
            <h2 className="font-semibold text-xl">Tus listas</h2>
            <ListsDashboard className="mt-4" />
          </div>
        </div>
      </div>
    </>
  );
};
